import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Select, DatePicker, Row, Col, message, Tag, Tooltip } from "antd";
import Table from "../../../core/common/dataTable/index";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { filterByStatus } from "../../../core/utils/filterActiveData";
import { fetchAllDepartments } from "../../../core/data/redux/department/departmentAction";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchAllBranch } from "../../../core/data/redux/branch/branch.Action";
import type { ColumnsType } from "antd/es/table";
import { createShiftSchedule, fetchAllShiftSchedules, updateShiftSchedule } from "../../../core/data/redux/attendance/shiftSchedule/shiftScheduleAction";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { fetchAllWeekoff } from "../../../core/data/redux/weekoff/weekoff.Action";



dayjs.extend(isSameOrBefore);

type WeekPattern = Record<
  string,
  { all: boolean; weeks: Record<1 | 2 | 3 | 4 | 5, boolean> }
>;

const WEEK_OFF_VALUE = -1 as const;

export const normalizeWeekPattern = (raw: any): WeekPattern => {
  if (!raw) return {};
  if (typeof raw === "string") {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return raw;
};

const getWeekIndexInMonth = (date: Dayjs): 1 | 2 | 3 | 4 | 5 => {
  const idx = Math.floor((date.date() - 1) / 7) + 1;
  return idx as 1 | 2 | 3 | 4 | 5;
};

const isDateRangeValid = (start: Dayjs, end: Dayjs) =>
  end.diff(start, "month", true) <= 3;

interface EmployeeTableRow {
  key: number;
  name: string;
}

const { Option } = Select;

interface Props {
  showRosterDrawer: boolean;
  setShowRosterDrawer: (val: boolean) => void;
  holidays: any; // array of holidays
}

const ShiftRosterDrawer: React.FC<Props> = ({ showRosterDrawer, setShowRosterDrawer, holidays }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedDepartment, setSelectedDepartment] = useState<number | undefined>(undefined);
  const [selectedBranch, setSelectedBranch] = useState<number | undefined>(undefined);
  const { weekoff } = useSelector((state: any) => state.weekoff);
  const [tableKey, setTableKey] = useState(0);

  const [startDate, setStartDate] = useState<Dayjs>(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs().endOf("month"));

  const [shiftAssignments, setShiftAssignments] = useState<Record<string, number>>({});

  const { shiftSchedules = [] } = useSelector((state: any) => state.attendance.shiftSchedule);
  const { employees } = useSelector((state: any) => state.employee);
  const { shiftMasters = [] } = useSelector((state: any) => state.attendance.shift);
  const { departments } = useSelector((state: any) => state.department);
  const { branch } = useSelector((state: any) => state.branch);

  const activeEmployee = useMemo(
    () => filterByStatus(employees, "active", "employee_status"),
    [employees]
  );
  const activeDepartment = useMemo(
    () => filterByStatus(departments, "active", "Status"),
    [departments]
  );
  const ActiveBranch = useMemo(
    () => filterByStatus(branch, "active", "Status"),
    [branch]
  );

  const daysInRange = useMemo(() => {
    const days: Dayjs[] = [];
    let current = startDate.startOf("day");
    while (current.isSameOrBefore(endDate, "day")) {
      days.push(current);
      current = current.add(1, "day");
    }
    return days;
  }, [startDate, endDate]);

  // 🔒 Build a map of fixed holidays by date (YYYY-MM-DD) => { Name, Id }
  const fixedHolidayByDate = useMemo(() => {
    const map = new Map<string, { Name: string; Id: number }>();

    (holidays ?? []).forEach((h: any) => {
      const isActive = String(h?.Status ?? "").toLowerCase() === "active";
      const isFixed = String(h?.HolidayType ?? "").toLowerCase() === "fixed";

      if (!isActive || !isFixed) return;
      if (!h?.StartDate || !h?.EndDate) return;

      let start = dayjs(h.StartDate);
      let end = dayjs(h.EndDate);

      while (start.isSameOrBefore(end, "day")) {
        const d = start.format("YYYY-MM-DD");
        map.set(d, { Name: h?.Name ?? "Holiday", Id: Number(h?.Id) || 0 });

        start = start.add(1, "day");
      }
    });

    return map;
  }, [holidays]);


  useEffect(() => {
    if (!departments.length) dispatch(fetchAllDepartments());
  }, [departments.length, dispatch]);

  useEffect(() => {
    if (!branch.length) dispatch(fetchAllBranch());
  }, [branch.length, dispatch]);

  useEffect(() => {
    if (!Array.isArray(shiftSchedules)) return;

    const initial: Record<string, number> = {};
    shiftSchedules.forEach((schedule: any) => {
      if (!Array.isArray(schedule?.EmployeeIDs)) return;

      const shift = shiftMasters.find(
        (s: any) => s.ShiftID === Number(schedule.ShiftName) || s.ShiftName === schedule.ShiftName
      );
      const isWeeklyOff = String(schedule.ShiftName).trim().toLowerCase() === "weekly off";

      // If weekly off, proceed without shiftMasters
      if (!shift && !isWeeklyOff) return;

      const from = dayjs(schedule.FromDate);
      const to = dayjs(schedule.ToDate);

      daysInRange.forEach((d) => {
        if (d.isBetween(from, to, "day", "[]")) {
          const dateStr = d.format("YYYY-MM-DD");

          schedule.EmployeeIDs.forEach((empId: number) => {
            const isWeeklyOff =
              String(schedule.ShiftName).trim().toLowerCase() === "weekly off";

            // If schedule.ShiftName = "Weekly Off", store WEEK_OFF_VALUE in assignment
            initial[`${empId}_${dateStr}`] = isWeeklyOff
              ? WEEK_OFF_VALUE
              : (shift?.ShiftID ?? null);

          });
        }
      });

    });

    setShiftAssignments(initial);
  }, [shiftSchedules, daysInRange, shiftMasters]);

  const handleShiftChange = async (employeeId: number, dateStr: string, shiftId: number) => {
    // Guard: prevent editing on fixed holidays (extra safety)
    if (fixedHolidayByDate.has(dateStr)) {
      message.warning("This is a fixed holiday. You can't assign a shift here.");
      return;
    }

    setShiftAssignments((prev) => ({ ...prev, [`${employeeId}_${dateStr}`]: shiftId }));

    const selectedShift = shiftMasters.find((s: any) => s.ShiftID === shiftId);
    if (!selectedShift) return;

    const employee = activeEmployee.find((e: any) => e.EmployeeID === employeeId);
    const departmentId = employee ? employee.DepartmentId : null;

    const payload = {
      EmployeeIDs: `[${employeeId}]`,
      DepartmentID: departmentId,
      ShiftName: selectedShift.ShiftName,
      ShiftID: selectedShift.ShiftID,
      fromDate: dateStr,
      toDate: dateStr,
      StartTime: selectedShift.StartTime,
      EndTime: selectedShift.EndTime,
      BreakTime: selectedShift.BreakTime,
      MinStartTime: dayjs(selectedShift.StartTime, "HH:mm:ss").subtract(1, "hour").format("HH:mm:ss"),
      MaxStartTime: dayjs(selectedShift.StartTime, "HH:mm:ss").add(1, "hour").format("HH:mm:ss"),
      MinEndTime: dayjs(selectedShift.EndTime, "HH:mm:ss").subtract(1, "hour").format("HH:mm:ss"),
      MaxEndTime: dayjs(selectedShift.EndTime, "HH:mm:ss").add(1, "hour").format("HH:mm:ss"),
      AcceptExtraHours: true,
      IsPublished: false,
    };

    const existing = Array.isArray(shiftSchedules)
      ? shiftSchedules.find(
        (s: any) =>
          Array.isArray(s?.EmployeeIDs) &&
          s?.EmployeeIDs.includes(employeeId) &&
          dayjs(dateStr).isBetween(dayjs(s?.FromDate), dayjs(s?.ToDate), "day", "[]")
      )
      : undefined;

    if (existing) {
      await dispatch(updateShiftSchedule({ scheduleId: existing.ScheduleID, data: payload }));
    } else {
      await dispatch(createShiftSchedule(payload));
    }
    await dispatch(fetchAllShiftSchedules());
  };

  const handleWeeklyOffSelect = async (employeeId: number, dateStr: string) => {
    // guard: holiday
    if (fixedHolidayByDate.has(dateStr)) {
      message.warning("This is a fixed holiday. You can't change it.");
      return;
    }

    // remove local assignment
    setShiftAssignments((prev) => {
      const next = { ...prev };
      delete next[`${employeeId}_${dateStr}`];
      return next;
    });

    const weekOffPayload = {
      EmployeeIDs: `[${employeeId}]`,
      DepartmentID: activeEmployee.find((e: any) => e.EmployeeID === employeeId)?.DepartmentId ?? null,
      ShiftName: "Weekly Off",
      ShiftID: WEEK_OFF_VALUE,    // ⬅️ use -1 or a dedicated ID your backend accepts
      fromDate: dateStr,
      toDate: dateStr,
      StartTime: "00:00:00",
      EndTime: "00:00:00",
      BreakTime: "00:00:00",
      MinStartTime: "00:00:00",
      MaxStartTime: "00:00:00",
      MinEndTime: "00:00:00",
      MaxEndTime: "00:00:00",
      AcceptExtraHours: false,
      IsPublished: false,
    };

    const existing = Array.isArray(shiftSchedules)
      ? shiftSchedules.find(
        (s: any) =>
          Array.isArray(s?.EmployeeIDs) &&
          s.EmployeeIDs.includes(employeeId) &&
          dayjs(dateStr).isBetween(dayjs(s.FromDate), dayjs(s.ToDate), "day", "[]")
      )
      : undefined;

    if (existing) {
      await dispatch(
        updateShiftSchedule({
          scheduleId: existing.ScheduleID,
          data: weekOffPayload,
        })
      );
    } else {
      await dispatch(createShiftSchedule(weekOffPayload));
    }

    await dispatch(fetchAllShiftSchedules());
  };


  const employeeWeeklyOffMap = useMemo(() => {
    const toIdArray = (appliedTo: any): number[] => {
      if (appliedTo == null) return [];
      try {
        const parsed = typeof appliedTo === "string" ? JSON.parse(appliedTo) : appliedTo;
        if (Array.isArray(parsed)) return parsed.map((x) => Number(x));
      } catch { }
      const n = Number(appliedTo);
      return Number.isFinite(n) ? [n] : [];
    };

    const empIndex = new Map<number, Map<string, Set<number>>>();
    const deptIndex = new Map<number, Map<string, Set<number>>>();
    const branchIndex = new Map<number, Map<string, Set<number>>>();

    const pushWeeks = (
      idx: Map<number, Map<string, Set<number>>>,
      id: number,
      wp: WeekPattern
    ) => {
      const current = idx.get(id) ?? new Map<string, Set<number>>();
      Object.entries(wp).forEach(([day, row]) => {
        const set = current.get(day) ?? new Set<number>();
        if (row?.all) {
          [1, 2, 3, 4, 5].forEach((w) => set.add(w));
        } else if (row?.weeks) {
          (Object.entries(row.weeks) as [any, boolean][])?.forEach(([wk, val]) => {
            if (val) set.add(Number(wk));
          });
        }
        if (set.size) current.set(day, set);
      });
      idx.set(id, current);
    };

    (weekoff ?? []).forEach((item: any) => {
      if (item?.Status?.toLowerCase() !== "active") return;

      const ids = toIdArray(item?.AppliedTo);
      const wp = normalizeWeekPattern(item?.WeekPattern);
      if (!ids.length) return;

      const type = String(item?.Type || "").toLowerCase();
      if (type === "employee") ids.forEach((id) => pushWeeks(empIndex, id, wp));
      else if (type === "department") ids.forEach((id) => pushWeeks(deptIndex, id, wp));
      else if (type === "branch") ids.forEach((id) => pushWeeks(branchIndex, id, wp));
    });

    const result: Record<number, Map<string, Set<number>>> = {};
    (activeEmployee ?? []).forEach((emp: any) => {
      const empId = Number(emp?.EmployeeID);
      if (!Number.isFinite(empId)) return;

      const empWeeks = empIndex.get(empId);
      if (empWeeks) { result[empId] = empWeeks; return; }

      const deptId = Number(emp?.DepartmentId);
      const deptWeeks = Number.isFinite(deptId) ? deptIndex.get(deptId) : undefined;
      if (deptWeeks) { result[empId] = deptWeeks; return; }

      const brId = Number(emp?.branch ?? emp?.BranchId ?? emp?.BranchID);
      const brWeeks = Number.isFinite(brId) ? branchIndex.get(brId) : undefined;
      if (brWeeks) { result[empId] = brWeeks; return; }
    });

    return result;
  }, [weekoff, activeEmployee]);

  useEffect(() => {
    dispatch(fetchAllWeekoff());
  }, [dispatch]);

  const filteredEmployees = useMemo(() => {
    return activeEmployee.filter((emp) => {
      const departmentMatch = selectedDepartment ? emp.DepartmentId === selectedDepartment : true;
      const branchMatch = selectedBranch ? emp.branch === selectedBranch : true;
      return departmentMatch && branchMatch;
    });
  }, [activeEmployee, selectedDepartment, selectedBranch]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [filteredEmployees, daysInRange]);

  const columns: ColumnsType<EmployeeTableRow> = useMemo(() => {
    return [
      {
        title: "Employee",
        dataIndex: "name",
        key: "name",
        fixed: "left",
        width: 140,
      },
      ...daysInRange.map((date) => {
        const dayNum = date.date();
        const dateStr = date.format("YYYY-MM-DD");
        const dayLabel = date.format("dddd");
        const weekIndex = getWeekIndexInMonth(date);

        return {
          title: `${dayNum} (${date.format("ddd")})`,
          dataIndex: `day_${dateStr}`,
          key: `day_${dateStr}`,
          render: (_: any, record: any) => {
            // 🔒 If fixed holiday => lock cell (no Select)
            const fixedHoliday = fixedHolidayByDate.get(dateStr);
            if (fixedHoliday) {
              return (
                <Tooltip title={`${fixedHoliday.Name} — Fixed Holiday`}>
                  <div style={{ width: 120, display: "flex", flexDirection: "column", gap: 4 }}>
                    <Tag color="red" style={{ width: "fit-content" }}>Holiday {fixedHoliday.Name}</Tag>
                    {/* <div style={{ fontSize: 12, color: "#555" }}>{fixedHoliday.Name}</div> */}
                  </div>
                </Tooltip>
              );
            }

            const mapForEmp = employeeWeeklyOffMap[record.key]; // Map<DayName, Set<WeekIndex>>
            const isWeeklyOffByPattern = mapForEmp?.get(dayLabel)?.has(weekIndex) ?? false;

            const key = `${record.key}_${dateStr}`;
            const assignedShiftId = shiftAssignments[key];

            const selectedValue =
              assignedShiftId !== undefined
                ? assignedShiftId
                : (isWeeklyOffByPattern ? WEEK_OFF_VALUE : undefined);

            const onChange = (value: number) => {
              if (fixedHolidayByDate.has(dateStr)) {
                message.warning("This is a fixed holiday. You can't change it.");
                return;
              }
              if (value === WEEK_OFF_VALUE) {
                handleWeeklyOffSelect(record.key, dateStr);
              } else {
                handleShiftChange(record.key, dateStr, value);
              }
            };

            return (
              <Select
                placeholder="Shift"
                style={{ width: 120 }}
                value={selectedValue}
                onChange={onChange}
                dropdownMatchSelectWidth={false}
              >
                <Option value={WEEK_OFF_VALUE} label="Weekly Off">
                  <span style={{ color: "#ff4d4f", fontWeight: 600 }}>Weekly Off</span>
                </Option>
                {shiftMasters?.map((shift: any) => (
                  <Option key={shift?.ShiftID} value={shift?.ShiftID}>
                    {shift?.ShiftName}
                  </Option>
                ))}
              </Select>
            );
          },
        };
      }),
    ];
  }, [
    daysInRange,
    shiftAssignments,
    shiftMasters,
    employeeWeeklyOffMap,
    fixedHolidayByDate,
  ]);

  const dataSource = useMemo(
    () => filteredEmployees?.map((emp) => ({ key: emp.EmployeeID, name: emp.Name })),
    [filteredEmployees]
  );

  return (
    <Drawer
      title="Shift Roster"
      placement="right"
      width={1000}
      onClose={() => setShowRosterDrawer(false)}
      open={showRosterDrawer}
      bodyStyle={{ padding: 0 }}
      style={{ marginTop: 50, paddingBottom: 50 }}
    >
      <div style={{ padding: 16 }}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Select
              placeholder="Select Department"
              value={selectedDepartment}
              onChange={(val) => setSelectedDepartment(val)}
              style={{ width: "100%" }}
              allowClear
            >
              {activeDepartment?.map((dep: any) => (
                <Option key={dep.Id} value={dep.Id}>{dep.Name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="Select Branch"
              value={selectedBranch}
              onChange={(val) => setSelectedBranch(val)}
              style={{ width: "100%" }}
              allowClear
            >
              {ActiveBranch?.map((br: any) => (
                <Option key={br.Id} value={br.Id}>{br.Branch_Name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <DatePicker
              placeholder="Start Date"
              style={{ width: "100%" }}
              value={startDate}
              onChange={(date) => {
                if (!date) return setStartDate(dayjs().startOf("month"));
                if (endDate && date.isAfter(endDate, "day")) {
                  message.warning("Start date cannot be after end date.");
                  return;
                }

                if (!isDateRangeValid(date, endDate)) {
                  message.warning("Date range cannot exceed 3 months.");
                  return;
                }
                if (!isDateRangeValid(date, endDate)) {
                  return message.warning("Date range cannot exceed 3 months.");
                }
                setStartDate(date.startOf("day"));
              }}
              disabledDate={(current) => current && current.isAfter(dayjs().endOf("year"))}
            />
          </Col>
          <Col span={6}>
            <DatePicker
              placeholder="End Date"
              style={{ width: "100%" }}
              value={endDate}
              onChange={(date) => {
                if (!date) return setEndDate(dayjs().endOf("month"));
                if (startDate && date.isBefore(startDate, "day")) {
                  message.warning("End date cannot be before start date.");
                  return;
                }

                if (!isDateRangeValid(startDate, date)) {
                  message.warning("Date range cannot exceed 3 months.");
                  return;
                }
                if (!isDateRangeValid(startDate, date)) {
                  return message.warning("Date range cannot exceed 3 months.");
                }
                setEndDate(date.endOf("day"));
              }}
              disabledDate={(current) => current && current.isAfter(dayjs().endOf("year"))}
            />
          </Col>
        </Row>

        <Table
          key={tableKey}
          dataSource={dataSource}
          columns={columns}
          Selection={true}
        />
      </div>
    </Drawer>
  );
};

export default ShiftRosterDrawer;
